import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";
import TablePagination from "./TablePagination";
import { selectStyles } from "../helper/SelectStyles";
import { getClientGroups, createClientGroup, deleteClientGroup, updateClientGroup, getClientsInGroup, assignGroupClients } from "../Apis/ClientGroupAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { bulkUpload, downloadSample } from "../Apis/ClientAPIs";
import { server } from "../Apis/api";
import usePermissions from "../hook/userPermission";

const ClientGroupLayer = () => {
  const { hasPermission } = usePermissions();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Bulk Upload States
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [showErrorDownloadModal, setShowErrorDownloadModal] = useState(false);
  const [errorDownloadUrl, setErrorDownloadUrl] = useState("");
  const [errorCount, setErrorCount] = useState(0);

  // Assign Clients States
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [targetGroupForAssign, setTargetGroupForAssign] = useState(null);
  const [expoGroups, setExpoGroups] = useState([]);
  const [selectedSourceGroup, setSelectedSourceGroup] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [assignMode, setAssignMode] = useState("all");
  const [sourceGroupClients, setSourceGroupClients] = useState([]);
  const [checkedClientIds, setCheckedClientIds] = useState([]);
  const [isFetchingClients, setIsFetchingClients] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Managers list
  const [managers, setManagers] = useState([]);

  // Add Group Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addGroupName, setAddGroupName] = useState("");
  const [addGroupType, setAddGroupType] = useState("Manager");
  const [addManagerId, setAddManagerId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Edit Group Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupType, setEditGroupType] = useState("Manager");
  const [editManagerId, setEditManagerId] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteWarningMsg, setDeleteWarningMsg] = useState("");

  // Debounce search
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(h);
  }, [searchTerm]);

  const fetchGroups = async () => {
    setIsLoading(true);
    const res = await getClientGroups({
      page: currentPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
    });
    if (res.status) {
      setGroups(res.response?.data || []);
      setTotalRecords(res.response?.total || 0);
    }
    setIsLoading(false);
  };

  const fetchManagers = async () => {
    const res = await getManagers({ isActive: true, limit: 1000 });
    if (res.status) {
      const data = res.response?.data || res.response || [];
      setManagers(
        data.map((m) => ({ value: m._id || m.id, label: m.name }))
      );
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [currentPage, rowsPerPage, debouncedSearch]);

  const fetchLocations = async () => {
    const res = await getLocations({ limit: 1000 });
    if (res.status) {
      const data = res.response?.data || res.response || [];
      setLocations(
        data.map((loc) => ({ value: loc._id || loc.id, label: loc.name || loc.locationName }))
      );
    }
  };

  const fetchExpoGroups = async () => {
    const res = await getClientGroups({
      limit: 1000,
      groupType: "Expo"
    });
    if (res.status) {
      const data = res.response?.data || [];
      setExpoGroups(
        data.map((g) => ({ value: g._id || g.id, label: g.groupName }))
      );
    }
  };

  const fetchSourceClients = async (sourceGroupId, locationId) => {
    if (!sourceGroupId) return;
    setIsFetchingClients(true);
    const res = await getClientsInGroup(sourceGroupId, {
      locationId: locationId || undefined
    });
    if (res.status) {
      const list = res.response?.data || [];
      setSourceGroupClients(list);
      // Select all by default
      setCheckedClientIds(list.map(c => c._id || c.id));
    } else {
      setSourceGroupClients([]);
      setCheckedClientIds([]);
    }
    setIsFetchingClients(false);
  };

  useEffect(() => {
    fetchManagers();
    fetchLocations();
    fetchExpoGroups();
  }, []);

  useEffect(() => {
    if (showAssignModal && selectedSourceGroup) {
      fetchSourceClients(selectedSourceGroup.value, selectedLocation?.value);
    }
  }, [selectedSourceGroup, selectedLocation, showAssignModal]);

  // Assign Handlers
  const handleAssignClick = (group) => {
    setTargetGroupForAssign(group);
    setSelectedSourceGroup(null);
    setSelectedLocation(null);
    setAssignMode("all");
    setSourceGroupClients([]);
    setCheckedClientIds([]);
    setShowAssignModal(true);
    fetchExpoGroups(); // Refresh expo groups to ensure latest
  };

  const handleCheckboxChange = (clientId) => {
    setCheckedClientIds(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAllChecked = (e) => {
    if (e.target.checked) {
      setCheckedClientIds(sourceGroupClients.map(c => c._id || c.id));
    } else {
      setCheckedClientIds([]);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedSourceGroup) {
      return toast.error("Please select a source database group");
    }

    let clientIds = "all";
    if (assignMode === "partial") {
      if (checkedClientIds.length === 0) {
        return toast.error("Please check at least one client to assign");
      }
      clientIds = checkedClientIds;
    }

    setIsAssigning(true);
    const res = await assignGroupClients(targetGroupForAssign._id || targetGroupForAssign.id, {
      clientIds,
      sourceGroupId: selectedSourceGroup.value,
      locationId: selectedLocation?.value || undefined
    });

    if (res.status) {
      toast.success(res.response?.message || "Clients copied and assigned successfully");
      setShowAssignModal(false);
      fetchGroups();
    }
    setIsAssigning(false);
  };

  // Add Group
  const handleAddGroup = async () => {
    if (!addGroupName.trim()) return toast.error("Please enter a Database name");
    if (addGroupType === "Manager" && !addManagerId) {
      return toast.error("Please select a manager to assign this group to");
    }

    setIsSaving(true);
    const res = await createClientGroup({
      groupName: addGroupName.trim(),
      groupType: addGroupType,
      managerId: addGroupType === "Manager" ? addManagerId?.value || "" : "",
    });
    if (res.status) {
      toast.success("Group created successfully");
      setShowAddModal(false);
      setAddGroupName("");
      setAddGroupType("Manager");
      setAddManagerId(null);
      fetchGroups();
    }
    setIsSaving(false);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddGroupName("");
    setAddGroupType("Manager");
    setAddManagerId(null);
  };

  // Edit Group
  const handleEditClick = (group) => {
    setEditGroupId(group._id || group.id);
    setEditGroupName(group.groupName);
    setEditGroupType(group.groupType || "Manager");
    const mId = group.manager?._id || group.manager?.id || null;
    setEditManagerId(mId ? { value: mId, label: group.manager?.name } : null);
    setShowEditModal(true);
  };

  const handleUpdateGroup = async () => {
    if (!editGroupName.trim()) return toast.error("Please enter a Database name");
    if (editGroupType === "Manager" && !editManagerId) {
      return toast.error("Please select a manager to assign this group to");
    }

    setIsSavingEdit(true);
    const res = await updateClientGroup(editGroupId, {
      groupName: editGroupName.trim(),
      groupType: editGroupType,
      managerId: editGroupType === "Manager" ? editManagerId?.value || "" : "",
    });
    if (res.status) {
      toast.success("Group updated successfully");
      setShowEditModal(false);
      setEditGroupId(null);
      setEditGroupName("");
      setEditGroupType("Manager");
      setEditManagerId(null);
      fetchGroups();
    }
    setIsSavingEdit(false);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditGroupId(null);
    setEditGroupName("");
    setEditGroupType("Manager");
    setEditManagerId(null);
  };

  // Delete Group
  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setDeleteWarningMsg("");
    setShowDeleteModal(true);
  };

  const confirmDelete = async (force = false) => {
    if (!groupToDelete) return;
    setIsDeleting(true);
    const params = force === true ? { confirmDeleteData: "true" } : undefined;
    const res = await deleteClientGroup(groupToDelete._id || groupToDelete.id, params);
    if (res.status) {
      if (res.response?.hasAssociatedData) {
        setDeleteWarningMsg(res.response.message);
      } else {
        toast.success("Group deleted successfully");
        setShowDeleteModal(false);
        setGroupToDelete(null);
        setDeleteWarningMsg("");
        fetchGroups();
      }
    }
    setIsDeleting(false);
  };

  // Bulk Upload Handlers
  const handleBulkUploadClick = (group) => {
    setSelectedGroup(group);
    setSelectedFile(null);
    setShowBulkUploadModal(true);
  };

  const handleDownloadSample = async () => {
    const groupId = selectedGroup?._id || selectedGroup?.id || "";
    const managerId =
      typeof selectedGroup?.manager === "object"
        ? selectedGroup?.manager?._id || selectedGroup?.manager?.id || ""
        : selectedGroup?.manager || "";
    await downloadSample({ groupId, managerId });
  };

  const handleImportClients = async () => {
    if (!selectedFile || !selectedGroup) return;

    setIsBulkUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    const gId = selectedGroup._id || selectedGroup.id || "";

    formData.append("managerId", "");
    formData.append("groupId", gId);

    const res = await bulkUpload(formData);
    const errUrl = res.response?.data?.errorFileUrl || res.response?.errorFileUrl;
    const errCount = res.response?.data?.errorCount || res.response?.errorCount || 0;

    if (res.status) {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
      } else {
        toast.success(res.response?.message || "Clients Uploaded Successfully!");
        setSelectedGroup(null);
      }
      setShowBulkUploadModal(false);
      setSelectedFile(null);
      fetchGroups();
    } else {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
        setShowBulkUploadModal(false);
        setSelectedFile(null);
      }
    }
    setIsBulkUploading(false);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between"
        style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}
      >
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* <Link
            to="/clients"
            className="btn btn-sm d-flex align-items-center gap-1 px-10 py-6 radius-8"
            style={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white", border: "1px solid rgba(255,255,255,0.3)", fontWeight: "600" }}
          >
            <Icon icon="lucide:arrow-left" className="text-lg" />
            Back
          </Link> */}
          <h6 className="text-white pb-2 mb-0">Database Groups</h6>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="position-relative" style={{ minWidth: "250px" }}>
            <input
              type="text"
              className="form-control radius-8 ps-40 py-10 bg-white border-0"
              style={{ height: "44px" }}
              placeholder="Search group..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="position-absolute translate-middle-y top-50 start-0 ms-12 text-secondary-light"
              style={{ fontSize: "20px" }}
            />
          </div>
          <button
            className="btn text-sm btn-sm px-12 py-8 radius-8 d-flex align-items-center gap-2"
            style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
            onClick={() => setShowAddModal(true)}
          >
            <Icon icon="ic:baseline-plus" className="text-xl" />
            Add Group
          </button>
        </div>
      </div>

      <div className="card-body p-24">

        {/* Table */}
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr style={{ backgroundColor: "#f7e7da" }}>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Database Name</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Group Type</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Assigned Manager</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>No of Exhibitors</th>
                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Action</th>
                {/* <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Bulk Upload</th> */}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">Loading...</td>
                </tr>
              ) : groups.length > 0 ? (
                groups.map((group, index) => (
                  <tr key={group._id}>
                    <td>{currentPage * rowsPerPage + index + 1}</td>
                    <td>
                      <span className="fw-bold">{group.groupName}</span>
                    </td>
                    <td>
                      <span className={`badge ${group.groupType === 'Expo' ? 'bg-info-focus text-info-600' : 'bg-success-focus text-success-600'} px-12 py-4 radius-pill fw-bold`}>
                        {group.groupType || 'Manager'}
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary-light fw-medium">{group.manager?.name || "-"}</span>
                    </td>
                    <td>
                      <span className="badge bg-primary-focus text-primary-600 px-12 py-4 radius-pill fw-bold">
                        {group.clientCount || 0}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <Link
                          to={`/client-groups/view/${group._id || group.id}`}
                          className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          style={{
                            backgroundColor: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid #e0e7ff",
                          }}
                          title="View"
                        >
                          <Icon icon="lucide:eye" className="menu-icon" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleEditClick(group)}
                          className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          title="Edit"
                          style={{
                            backgroundColor: "#fef3c7",
                            color: "#d97706",
                            border: "1px solid #fef3c7",
                          }}
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(group)}
                          className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                          title="Delete"
                        >
                          <Icon icon="lucide:trash-2" className="text-xl" />
                        </button>
                      </div>
                    </td>
                    {/* <td>
                      <div className="d-flex align-items-center gap-2">
                        {hasPermission("clients", "add") && (
                          <button
                            type="button"
                            onClick={() => handleBulkUploadClick(group)}
                            className="btn btn-outline-danger btn-sm radius-8 d-flex align-items-center gap-2 fw-bold"
                            title="Bulk Upload"
                          >
                            <Icon icon="solar:upload-minimalistic-bold" className="text-lg line-height-1" />
                            Bulk Upload
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAssignClick(group)}
                          className="btn btn-outline-primary btn-sm radius-8 d-flex align-items-center gap-2 fw-bold"
                          title="Assign from Existing Group"
                        >
                          <Icon icon="solar:users-group-two-rounded-bold" className="text-lg line-height-1" />
                          Assign Clients
                        </button>
                      </div>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4">No groups found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
          onPageChange={setCurrentPage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }}
          totalRecords={totalRecords}
        />
      </div>

      {/* Add Group Modal */}
      <Modal show={showAddModal} onHide={handleCloseAddModal} centered size="md">
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{ background: "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)" }}
          >
            <h6 className="text-white mb-0">Add Client Group</h6>
            <button type="button" className="btn-close btn-close-white" onClick={handleCloseAddModal} />
          </div>
          <div className="modal-body p-32">

            <div className="mb-4">
              <label className="form-label fw-bold">
                Database Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control radius-8"
                style={{ height: "44px" }}
                placeholder="Enter database name..."
                value={addGroupName}
                onChange={(e) => setAddGroupName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddGroup()}
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Group Type</label>
              <div className="d-flex gap-4">
                <div className="form-check d-flex align-items-center gap-2 mb-0">
                  <input
                    className="form-check-input cursor-pointer shadow-none"
                    type="radio"
                    name="addGroupType"
                    id="addGroupTypeManager"
                    checked={addGroupType === "Manager"}
                    onChange={() => setAddGroupType("Manager")}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "0",
                      accentColor: "#ed1c24",
                    }}
                  />
                  <label
                    className="form-check-label fw-bold mb-0 cursor-pointer"
                    htmlFor="addGroupTypeManager"
                  >
                    Manager-based
                  </label>
                </div>
                <div className="form-check d-flex align-items-center gap-2 mb-0">
                  <input
                    className="form-check-input cursor-pointer shadow-none"
                    type="radio"
                    name="addGroupType"
                    id="addGroupTypeExpo"
                    checked={addGroupType === "Expo"}
                    onChange={() => setAddGroupType("Expo")}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "0",
                      accentColor: "#ed1c24",
                    }}
                  />
                  <label
                    className="form-check-label fw-bold mb-0 cursor-pointer"
                    htmlFor="addGroupTypeExpo"
                  >
                    Expo-based
                  </label>
                </div>
              </div>
            </div>

            {addGroupType === "Manager" && (
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Assign Manager <span className="text-danger">*</span>
                </label>
                <Select
                  options={managers}
                  value={addManagerId}
                  onChange={(val) => setAddManagerId(val)}
                  styles={selectStyles()}
                  placeholder="Select Manager..."
                  isClearable
                  menuPortalTarget={document.body}
                  maxMenuHeight={250}
                />
              </div>
            )}
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-32 radius-8 fw-bold" onClick={handleCloseAddModal}>
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={isSaving}
              onClick={handleAddGroup}
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Create Group
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Group Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered size="md">
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{ background: "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)" }}
          >
            <h6 className="text-white mb-0">Edit Client Group</h6>
            <button type="button" className="btn-close btn-close-white" onClick={handleCloseEditModal} />
          </div>
          <div className="modal-body p-32">

            <div className="mb-4">
              <label className="form-label fw-bold">
                Database Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control radius-8"
                style={{ height: "44px" }}
                placeholder="Enter database name..."
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleUpdateGroup()}
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Group Type</label>
              <div className="d-flex gap-4">
                <div className="form-check d-flex align-items-center gap-2 mb-0">
                  <input
                    className="form-check-input cursor-pointer shadow-none"
                    type="radio"
                    name="editGroupType"
                    id="editGroupTypeManager"
                    checked={editGroupType === "Manager"}
                    onChange={() => setEditGroupType("Manager")}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "0",
                      accentColor: "#ed1c24",
                    }}
                  />
                  <label
                    className="form-check-label fw-bold mb-0 cursor-pointer"
                    htmlFor="editGroupTypeManager"
                  >
                    Manager-based
                  </label>
                </div>
                <div className="form-check d-flex align-items-center gap-2 mb-0">
                  <input
                    className="form-check-input cursor-pointer shadow-none"
                    type="radio"
                    name="editGroupType"
                    id="editGroupTypeExpo"
                    checked={editGroupType === "Expo"}
                    onChange={() => setEditGroupType("Expo")}
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "0",
                      accentColor: "#ed1c24",
                    }}
                  />
                  <label
                    className="form-check-label fw-bold mb-0 cursor-pointer"
                    htmlFor="editGroupTypeExpo"
                  >
                    Expo-based
                  </label>
                </div>
              </div>
            </div>

            {editGroupType === "Manager" && (
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Assign Manager <span className="text-danger">*</span>
                </label>
                <Select
                  options={managers}
                  value={editManagerId}
                  onChange={(val) => setEditManagerId(val)}
                  styles={selectStyles()}
                  placeholder="Select Manager..."
                  isClearable
                  menuPortalTarget={document.body}
                  maxMenuHeight={250}
                />
              </div>
            )}
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button variant="outline-secondary" className="px-32 radius-8 fw-bold" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={isSavingEdit}
              onClick={handleUpdateGroup}
            >
              {isSavingEdit ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setGroupToDelete(null); setDeleteWarningMsg(""); }} centered>
        <Modal.Body className="text-center p-5">
          {deleteWarningMsg ? (
            <>
              <div className="d-flex justify-content-center mb-3">
                <div className="bg-warning-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                  <Icon icon="solar:danger-triangle-bold" className="text-warning-600 text-xxl" />
                </div>
              </div>
              <h5 className="mb-3 text-warning-main">Cascade Delete Warning</h5>
              <p className="text-secondary-light mb-4 fw-medium">
                {deleteWarningMsg}
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button variant="outline-secondary" className="px-32 fw-bold" onClick={() => { setShowDeleteModal(false); setGroupToDelete(null); setDeleteWarningMsg(""); }}>
                  No, Cancel
                </Button>
                <Button
                  className="px-32 border-0 fw-bold text-white"
                  onClick={() => confirmDelete(true)}
                  disabled={isDeleting}
                  style={{ backgroundColor: "#ed1c24" }}
                >
                  {isDeleting ? "Deleting..." : "Yes, Delete All"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-center mb-3">
                <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                  <Icon icon="lucide:trash-2" className="text-danger-600 text-xxl" />
                </div>
              </div>
              <h5 className="mb-3">Are you sure?</h5>
              <p className="text-secondary-light mb-4">
                Do you want to delete group "<strong>{groupToDelete?.groupName}</strong>"?
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button variant="outline-secondary" className="px-32" onClick={() => { setShowDeleteModal(false); setGroupToDelete(null); }}>
                  Cancel
                </Button>
                <Button
                  className="px-32 border-0 text-white"
                  onClick={() => confirmDelete(false)}
                  disabled={isDeleting}
                  style={{ backgroundColor: "#ed1c24" }}
                >
                  {isDeleting ? "Deleting..." : "Confirm"}
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        show={showBulkUploadModal}
        onHide={() => {
          setShowBulkUploadModal(false);
          setSelectedFile(null);
          setSelectedGroup(null);
        }}
        centered
        size="md"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Clients</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
                setSelectedGroup(null);
              }}
            ></button>
          </div>
          <div className="modal-body p-32">
            {selectedGroup && (
              <div className="mb-16 p-12 radius-8 bg-neutral-100 border text-center">
                <span className="text-xs fw-semibold text-secondary-light uppercase d-block mb-4">Target Group</span>
                <h6 className="mb-0" style={{ color: "#ed1c24" }}>{selectedGroup.groupName}</h6>
              </div>
            )}

            <div className="text-center mb-24">
              <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-16">
                <Icon
                  icon="solar:upload-minimalistic-bold"
                  className="text-danger-600 text-xxl"
                />
              </div>
              <p className="text-secondary-light">
                Download the template, fill in your client data, and upload it
                back to the system.
              </p>
            </div>

            <div className="d-flex flex-column gap-4">
              {/* Download Section */}
              <div className="p-20 radius-12 border bg-neutral-50 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-sm mb-4 fw-bold">
                    Step 1: Download Template
                  </h6>
                  <p className="text-xs text-secondary-light mb-0">
                    Get the standard Excel/CSV format.
                  </p>
                </div>
                <button
                  className="btn btn-outline-danger btn-sm radius-8 d-flex align-items-center gap-2 fw-bold"
                  onClick={handleDownloadSample}
                >
                  <Icon icon="solar:download-bold" />
                  Download
                </button>
              </div>

              {/* Upload Section */}
              <div className="p-20 radius-12 border bg-neutral-50">
                <h6 className="text-sm mb-12 fw-bold">Step 2: Upload File</h6>
                <div
                  className="border-dashed radius-12 p-24 text-center cursor-pointer transition-all"
                  style={{
                    borderColor: "#ed1c24",
                    backgroundColor: "rgba(237, 28, 36, 0.02)",
                    border: "2px dashed #ed1c24",
                  }}
                  onClick={() =>
                    document.getElementById("bulkFileInput").click()
                  }
                >
                  <input
                    type="file"
                    id="bulkFileInput"
                    className="d-none"
                    accept=".csv, .xlsx, .xls"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  {selectedFile ? (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <Icon
                        icon="solar:file-check-bold"
                        className="text-success text-2xl"
                      />
                      <span className="text-sm fw-bold text-dark">
                        {selectedFile.name}
                      </span>
                      <Icon
                        icon="line-md:close"
                        className="text-danger cursor-pointer ms-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          const fileInput = document.getElementById("bulkFileInput");
                          if (fileInput) fileInput.value = "";
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <Icon
                        icon="solar:cloud-upload-bold"
                        className="text-danger-600 text-4xl mb-8"
                      />
                      <p className="text-sm text-secondary-light mb-0">
                        Click to browse or drag & drop files here
                      </p>
                      <p className="text-xs text-secondary-light opacity-75">
                        Supported formats: CSV, Excel
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32 radius-8 fw-bold"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
                setSelectedGroup(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={!selectedFile || isBulkUploading}
              onClick={handleImportClients}
            >
              {isBulkUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Upload Clients
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Error Download Modal */}
      <Modal
        show={showErrorDownloadModal}
        onHide={() => {
          setShowErrorDownloadModal(false);
          setErrorDownloadUrl("");
          setErrorCount(0);
          setSelectedGroup(null);
        }}
        centered
        size="md"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Errors</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowErrorDownloadModal(false);
                setErrorDownloadUrl("");
                setErrorCount(0);
                setSelectedGroup(null);
              }}
            ></button>
          </div>
          <div className="modal-body p-32 text-center">
            {selectedGroup && (
              <div className="mb-16 p-12 radius-8 bg-neutral-100 border text-center">
                <span className="text-xs fw-semibold text-secondary-light uppercase d-block mb-4">Database Name</span>
                <h6 className="mb-0 fw-bold" style={{ color: "#ed1c24" }}>{selectedGroup.groupName}</h6>
              </div>
            )}
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-24">
              <Icon
                icon="solar:danger-triangle-bold"
                className="text-danger-600 text-xxl"
              />
            </div>
            <h5 className="mb-12" style={{ color: "#ed1c24", fontWeight: "700" }}>
              Some Rows Have Errors!
            </h5>
            <p className="text-secondary-light mb-24">
              We found validation errors in <strong>{errorCount}</strong> row(s) of your uploaded file.
              Please download the error report below to view the specific errors and correct them.
            </p>

            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary px-32 radius-8 fw-bold"
                onClick={() => {
                  setShowErrorDownloadModal(false);
                  setErrorDownloadUrl("");
                  setErrorCount(0);
                  setSelectedGroup(null);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn px-32 radius-8 fw-bold text-white border-0 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#ed1c24" }}
                onClick={() => {
                  if (errorDownloadUrl) {
                    const link = document.createElement("a");
                    let downloadUrl = errorDownloadUrl;
                    if (!downloadUrl.startsWith("http")) {
                      const cleaned = downloadUrl.startsWith("/") ? downloadUrl : `/${downloadUrl}`;
                      downloadUrl = `${server}${cleaned}`;
                    }
                    link.href = downloadUrl;
                    link.setAttribute("download", `bulk_upload_errors_${Date.now()}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }
                }}
              >
                <Icon icon="solar:download-bold" />
                Download Error Report
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Assign/Copy Clients Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => {
          setShowAssignModal(false);
          setSelectedSourceGroup(null);
          setSelectedLocation(null);
        }}
        centered
        size="lg"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Assign Clients from Existing Group</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowAssignModal(false);
                setSelectedSourceGroup(null);
                setSelectedLocation(null);
              }}
            ></button>
          </div>
          <div className="modal-body p-32">
            {targetGroupForAssign && (
              <div className="mb-24 p-16 radius-12 bg-neutral-100 border text-center">
                <span className="text-xs fw-semibold text-secondary-light uppercase d-block mb-4">Target Database Group</span>
                <h5 className="mb-0 fw-bold" style={{ color: "#ed1c24" }}>{targetGroupForAssign.groupName}</h5>
              </div>
            )}

            <div className="row g-4">
              {/* Select Source Group */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Select Source Group (Expo-based) <span className="text-danger">*</span></label>
                <Select
                  options={expoGroups}
                  value={selectedSourceGroup}
                  onChange={(val) => {
                    setSelectedSourceGroup(val);
                    setSelectedLocation(null);
                  }}
                  styles={selectStyles()}
                  placeholder="Select Expo Group..."
                  isClearable
                  menuPortalTarget={document.body}
                />
              </div>

              {/* Filter by Location */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Filter by Interested Location</label>
                <Select
                  options={locations}
                  value={selectedLocation}
                  onChange={(val) => setSelectedLocation(val)}
                  styles={selectStyles()}
                  placeholder="All Locations..."
                  isClearable
                  menuPortalTarget={document.body}
                  disabled={!selectedSourceGroup}
                />
              </div>
            </div>

            {selectedSourceGroup && (
              <div className="mt-24">
                <div className="d-flex align-items-center justify-content-between mb-16 p-12 radius-8 bg-neutral-50 border">
                  <span className="fw-semibold text-secondary-light">
                    Clients Found in Source Group: <strong>{isFetchingClients ? "..." : sourceGroupClients.length}</strong>
                  </span>
                  {!isFetchingClients && sourceGroupClients.length > 0 && (
                    <div className="d-flex gap-4">
                      <div className="form-check d-flex align-items-center gap-2 mb-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="radio"
                          name="assignMode"
                          id="assignModeAll"
                          checked={assignMode === "all"}
                          onChange={() => setAssignMode("all")}
                          style={{ accentColor: "#ed1c24" }}
                        />
                        <label className="form-check-label mb-0 cursor-pointer fw-bold" htmlFor="assignModeAll">
                          Copy All Clients
                        </label>
                      </div>
                      <div className="form-check d-flex align-items-center gap-2 mb-0">
                        <input
                          className="form-check-input cursor-pointer"
                          type="radio"
                          name="assignMode"
                          id="assignModePartial"
                          checked={assignMode === "partial"}
                          onChange={() => setAssignMode("partial")}
                          style={{ accentColor: "#ed1c24" }}
                        />
                        <label className="form-check-label mb-0 cursor-pointer fw-bold" htmlFor="assignModePartial">
                          Select Partially
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {isFetchingClients ? (
                  <div className="text-center py-24">
                    <span className="spinner-border spinner-border-sm text-danger" role="status" aria-hidden="true"></span>
                    <span className="ms-8 fw-semibold text-secondary-light">Loading clients list...</span>
                  </div>
                ) : sourceGroupClients.length === 0 ? (
                  <div className="alert alert-warning text-center py-16 mb-0">
                    No clients found in this group with the selected location filter.
                  </div>
                ) : assignMode === "partial" ? (
                  <div className="border radius-12 p-16 bg-white overflow-y-auto scroll-sm" style={{ maxHeight: "250px" }}>
                    <div className="d-flex align-items-center gap-2 pb-12 mb-12 border-bottom">
                      <input
                        type="checkbox"
                        className="form-check-input cursor-pointer"
                        id="selectAllClients"
                        checked={checkedClientIds.length === sourceGroupClients.length}
                        onChange={handleSelectAllChecked}
                        style={{ accentColor: "#ed1c24" }}
                      />
                      <label htmlFor="selectAllClients" className="form-check-label cursor-pointer fw-bold mb-0 text-sm">
                        Select All ({checkedClientIds.length} / {sourceGroupClients.length})
                      </label>
                    </div>

                    <div className="d-flex flex-column gap-2">
                      {sourceGroupClients.map((client) => (
                        <div key={client._id || client.id} className="d-flex align-items-center gap-2 py-4">
                          <input
                            type="checkbox"
                            className="form-check-input cursor-pointer"
                            id={`client-${client._id || client.id}`}
                            checked={checkedClientIds.includes(client._id || client.id)}
                            onChange={() => handleCheckboxChange(client._id || client.id)}
                            style={{ accentColor: "#ed1c24" }}
                          />
                          <label
                            htmlFor={`client-${client._id || client.id}`}
                            className="form-check-label cursor-pointer mb-0 text-sm"
                          >
                            <span className="fw-bold">{client.companyName}</span> ({client.keyPerson} - {client.mobile1})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32 radius-8 fw-bold"
              onClick={() => {
                setShowAssignModal(false);
                setSelectedSourceGroup(null);
                setSelectedLocation(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={!selectedSourceGroup || isAssigning || (assignMode === "partial" && checkedClientIds.length === 0)}
              onClick={handleAssignSubmit}
            >
              {isAssigning ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Assigning...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Assign Clients
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientGroupLayer;
